
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 300;
const DEFAULT_GLOW_COLOR = '14, 165, 233'; // Sky Blue color matching the brand
const MOBILE_BREAKPOINT = 768;

interface CardData {
  title: string;
  description: string;
  label?: string;
  icon: React.ReactNode;
}

interface MagicBentoProps {
  data: CardData[];
  textAutoHide?: boolean;
  enableStars?: boolean;
  enableSpotlight?: boolean;
  enableBorderGlow?: boolean;
  disableAnimations?: boolean;
  spotlightRadius?: number;
  particleCount?: number;
  enableTilt?: boolean;
  glowColor?: string;
  clickEffect?: boolean;
  enableMagnetism?: boolean;
}

const GlobalSpotlight = ({
  gridRef,
  disableAnimations = false,
  enabled = true,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  glowColor = DEFAULT_GLOW_COLOR
}: any) => {
  const spotlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableAnimations || !gridRef?.current || !enabled) return;

    const spotlight = document.createElement('div');
    spotlight.className = 'global-spotlight';
    spotlight.style.cssText = `
      position: fixed;
      width: ${spotlightRadius * 2}px;
      height: ${spotlightRadius * 2}px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${glowColor}, 0.12) 0%,
        rgba(${glowColor}, 0.05) 30%,
        transparent 70%
      );
      z-index: 200;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
    document.body.appendChild(spotlight);
    spotlightRef.current = spotlight;

    const handleMouseMove = (e: MouseEvent) => {
      if (!spotlightRef.current || !gridRef.current) return;

      const rect = gridRef.current.getBoundingClientRect();
      const mouseInside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;

      const cards = gridRef.current.querySelectorAll('.bento-card-item');

      if (!mouseInside) {
        gsap.to(spotlightRef.current, { opacity: 0, duration: 0.5 });
        cards.forEach((card: any) => card.style.setProperty('--glow-intensity', '0'));
        return;
      }

      gsap.to(spotlightRef.current, {
        left: e.clientX,
        top: e.clientY,
        opacity: 1,
        duration: 0.2,
        ease: 'power2.out'
      });

      cards.forEach((card: any) => {
        const cRect = card.getBoundingClientRect();
        const relX = ((e.clientX - cRect.left) / cRect.width) * 100;
        const relY = ((e.clientY - cRect.top) / cRect.height) * 100;
        card.style.setProperty('--glow-x', `${relX}%`);
        card.style.setProperty('--glow-y', `${relY}%`);
        card.style.setProperty('--glow-intensity', '1');
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      spotlightRef.current?.remove();
    };
  }, [gridRef, disableAnimations, enabled, spotlightRadius, glowColor]);

  return null;
};

export const MagicBento = ({
  data,
  textAutoHide = true,
  enableStars = false,
  enableSpotlight = true,
  enableBorderGlow = true,
  disableAnimations = false,
  spotlightRadius = 230,
  glowColor = "14, 165, 233",
  enableTilt = false,
  enableMagnetism = false,
  clickEffect = false
}: MagicBentoProps) => {
  const gridRef = useRef<HTMLDivElement>(null);

  return (
    <div className="w-full">
      <style>{`
        .bento-card-item {
          --glow-x: 50%;
          --glow-y: 50%;
          --glow-intensity: 0;
          --glow-radius: ${spotlightRadius}px;
          background: #060010;
          border: 1px solid #1a1a1a;
          position: relative;
          overflow: hidden;
        }
        .bento-card-item::after {
          content: '';
          position: absolute;
          inset: 0;
          padding: 1px;
          background: radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y), 
            rgba(${glowColor}, calc(var(--glow-intensity) * 0.6)) 0%, 
            transparent 70%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.5s ease;
        }
        .bento-card-item:hover::after {
          opacity: 1;
        }
      `}</style>

      {enableSpotlight && <GlobalSpotlight gridRef={gridRef} spotlightRadius={spotlightRadius} glowColor={glowColor} />}

      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {data.map((item, idx) => (
          <div 
            key={idx} 
            className="bento-card-item group p-12 rounded-[32px] flex flex-col items-center text-center transition-all duration-500 hover:-translate-y-2 hover:border-sky-500/30"
          >
            <div className="mb-10 relative">
              <div className="absolute inset-0 bg-sky-500/20 blur-2xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative z-10 w-20 h-20 flex items-center justify-center bg-white/5 rounded-3xl border border-white/10 group-hover:border-sky-500/50 transition-all duration-500 group-hover:scale-110">
                {item.icon}
              </div>
            </div>
            
            {item.label && (
              <span className="text-xs font-black uppercase tracking-[0.25em] text-sky-400 mb-4 opacity-80">
                {item.label}
              </span>
            )}
            
            <h3 className="text-3xl font-bold text-white mb-5 tracking-tight group-hover:text-sky-100 transition-colors">
              {item.title}
            </h3>
            <p className="text-gray-400 text-2xl font-light leading-relaxed group-hover:text-gray-200 transition-colors">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
